import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
} from '@mui/material';
import {
  ChargeTypeInstance,
  PricingModel,
} from '../../../data/models/pricing-model.model';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import { useForm } from 'react-hook-form';
import ChargeTypeTable from './ChargeTypeTable';
import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as accountingSlice from '../../../data/accounting.slice';
import * as uiSlice from '../../../data/ui.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PricingModeDialog';

const classes = {
  content: `${PREFIX}-content`,
  nameInput: `${PREFIX}-nameInput`,
  message: `${PREFIX}-message`,
  table: `${PREFIX}-table`,
  actions: `${PREFIX}-actions`,
  buttons: `${PREFIX}-buttons`,
  addButtonContainer: `${PREFIX}-addButtonContainer`,
  addButton: `${PREFIX}-addButton`,
  actionButton: `${PREFIX}-actionButton`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.nameInput}`]: {
    width: 250,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'end',
  },

  [`& .${classes.buttons}`]: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  [`& .${classes.table} > div:first-of-type`]: {
    minHeight: 'auto',
  },
  [`& .${classes.addButtonContainer}`]: {
    display: 'flex',
    alignItems: 'begin',
  },
  [`& .${classes.addButton}`]: {
    ...theme.typography.body1,
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    marginBottom: 12,
    width: 72,
    height: 24,
  },

  [`& .${classes.actionButton}`]: {
    height: 48,
    width: 178,
  },
}));

export enum PricingModelDialogAction {
  Add = 'add',
  Edit = 'edit',
  Duplicate = 'duplicate',
  View = 'view',
}

interface PricingModeDialogProps {
  open: boolean;
  action?: PricingModelDialogAction;
  close: () => void;
  pricingModel?: PricingModel;
}

export default function PricingModelDialog({
  open,
  action,
  close,
  pricingModel,
}: PricingModeDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const isSuperReaderUser = useIsSuperReaderUser();
  const requiredFieldMessage = t('common.requiredField');
  const getDefaultChargeTypeInstance = () => ({
    chargeTypeBC: '',
    calculationGroupBC: '',
    calculationOverrideGroupBCs: [],
    pricingFunctionBC: '',
    isRequired: false,
    temporaryUUID: crypto.randomUUID(),
    isNew: true,
    isUnsaved: true,
  });
  const [rows, setRows] = useState<ChargeTypeInstance[]>([]);
  const [componentsValid, setComponentsValid] = useState(false);
  const savingPricingModel = useAppSelector(
    accountingSlice.selectSavingPricingModel,
  );
  const savedPricingModel = useAppSelector(
    accountingSlice.selectSavedPricingModel,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const isReadOnly =
    action === PricingModelDialogAction.View || isSuperReaderUser;

  const formHook = useForm({
    defaultValues: {
      id: '',
      name: '',
    },
  });

  useEffect(() => {
    if (action === PricingModelDialogAction.Add) {
      setRows([getDefaultChargeTypeInstance()]);
    }
  }, [action]);

  useEffect(() => {
    if (
      (action === PricingModelDialogAction.Edit ||
        action === PricingModelDialogAction.Duplicate ||
        action === PricingModelDialogAction.View) &&
      pricingModel
    ) {
      formHook.setValue('id', pricingModel?.pricingModelUUID || '');
      let name = pricingModel?.name ?? '';
      if (action === PricingModelDialogAction.Duplicate) {
        name = t('pricingmodel.duplicateOf', { name });
      }
      formHook.setValue('name', name);
      setRows([
        ...(pricingModel?.chargeTypes.map((ct) => ({
          ...ct,
          calculationOverrideGroupBCs: ct.calculationOverrideGroupBCs || [],
          temporaryUUID: crypto.randomUUID(),
        })) || []),
      ]);
    }
  }, [pricingModel, action]);

  useEffect(() => {
    if (saveSuccessMessage && savedPricingModel) {
      handleClose();
      dispatch(accountingSlice.setSavedPricingModel(false));
    }
  }, [saveSuccessMessage]);

  const handleClose = () => {
    formHook.reset();
    setRows([]);
    close();
  };

  const name = formHook.watch('name');

  const handleSave = (data: any) => {
    const pricingModel: PricingModel = {
      pricingModelUUID: data.id || undefined,
      name: data.name,
      chargeTypes: rows.map((row) => ({
        chargeTypeBC: row.chargeTypeBC,
        calculationGroupBC: row.calculationGroupBC,
        calculationOverrideGroupBCs: row.calculationOverrideGroupBCs || [],
        pricingFunctionBC: row.pricingFunctionBC,
        isRequired: row.isRequired,
      })),
    };
    if (action === PricingModelDialogAction.Edit) {
      dispatch(accountingSlice.updatePricingModelAsync(pricingModel));
    } else {
      if (action === PricingModelDialogAction.Duplicate) {
        pricingModel.pricingModelUUID = undefined;
      }
      dispatch(accountingSlice.createPricingModelAsync(pricingModel));
    }
  };

  const handleAddRowClick = () => {
    setRows((oldRows) => [...oldRows, getDefaultChargeTypeInstance()]);
  };

  return (
    <RootDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={true}>
      <DialogTitle>{t(`pricingmodel.${action}PricingModel`)}</DialogTitle>
      <DialogContent>
        <Stack direction="column" className={classes.content}>
          <TextInput
            name="name"
            label={t('pricingmodel.pricingModelName')}
            className={classes.nameInput}
            control={formHook.control}
            required={!isReadOnly}
            rules={{ required: requiredFieldMessage }}
            errors={formHook.formState.errors}
            readOnly={isReadOnly}
          />
          <Box className={classes.table}>
            <ChargeTypeTable
              readonly={isReadOnly}
              chargeTypeInstances={rows}
              setValid={setComponentsValid}
              setRows={setRows}
            />
          </Box>
          {!isReadOnly && (
            <Box className={classes.addButtonContainer}>
              <Button className={classes.addButton} onClick={handleAddRowClick}>
                + {t('pricingmodel.addRow')}
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box className={classes.buttons}>
          {isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.actionButton}
              onClick={handleClose}>
              {t('common.close')}
            </Button>
          )}
          {!isReadOnly && (
            <>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={classes.actionButton}
                disabled={savingPricingModel}
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.actionButton}
                disabled={!name || savingPricingModel || !componentsValid}
                onClick={formHook.handleSubmit(handleSave)}>
                {pricingModel ? t('common.save') : t('common.add')}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </RootDialog>
  );
}
